// Choosing several athletes for one programme.
//
// Pure logic only, paired with the "Several athletes" mode of Coaching.js. The
// server owns every rule — the 50-athlete cap, duplicates, the roster check —
// and refuses the whole list if any of it fails. This exists so the coach is
// told before the round trip, and so the request the screen sends can never
// carry a duplicate or a stale id the picker itself produced.

/**
 * The assign form's third mode. A UI choice only: it posts to
 * /assignments/bulk, which takes no mode because a list of individuals is only
 * ever a list of copies.
 */
export const BULK_MODE = 'several';

/** The server's cap (models.MaxBulkAssignees). */
export const MAX_BULK_ATHLETES = 50;

/** What the bulk mode means, beside the choice, like describeMode. */
export function describeBulkMode() {
  return {
    title: 'A copy each',
    detail: 'Every athlete you tick gets their own duplicate, all at once or not at '
      + 'all. Each diverges on its own, and each starts with an empty log.',
  };
}

const idOf = (value) => Number(value) || 0;

/** Whether an athlete is ticked. */
export function isSelected(selected, athleteId) {
  return (selected ?? []).includes(idOf(athleteId));
}

/**
 * Tick or untick one athlete. Returns a new list; never a duplicate, because a
 * duplicate is a 400 for the whole request.
 */
export function toggleAthlete(selected, athleteId) {
  const id = idOf(athleteId);
  const current = selected ?? [];
  if (!id) return current;
  return current.includes(id)
    ? current.filter((existing) => existing !== id)
    : [...current, id];
}

/** Tick everybody on the roster. */
export function selectAll(roster) {
  return [...new Set((roster ?? []).map((athlete) => idOf(athlete.athleteId)).filter(Boolean))];
}

/** Whether every athlete on a non-empty roster is ticked. */
export function isAllSelected(selected, roster) {
  const everyone = selectAll(roster);
  return everyone.length > 0 && everyone.every((id) => isSelected(selected, id));
}

/**
 * Drop ticks for anybody no longer on the roster.
 *
 * The roster reloads after every action on the screen. Removing an athlete
 * while they are ticked would otherwise keep their id in the list, invisible,
 * and the next bulk assignment would 404 for the whole squad over somebody the
 * coach can no longer see.
 */
export function pruneSelection(selected, roster) {
  const onRoster = new Set(selectAll(roster));
  return (selected ?? []).filter((id) => onRoster.has(id));
}

/** A one-line summary of who is ticked, in roster order. */
export function selectionSummary(selected, roster) {
  const chosen = (roster ?? []).filter((athlete) => isSelected(selected, athlete.athleteId));
  if (chosen.length === 0) return 'Nobody chosen yet.';
  const names = chosen.map((athlete) => athlete.displayName || athlete.username);
  const count = `${chosen.length} athlete${chosen.length === 1 ? '' : 's'}`;
  return `${count}: ${names.join(', ')}`;
}

/** Validate the bulk assignment. Returns a field-to-message map. */
export function validateBulkAssignment(form, selected) {
  const errors = {};
  if (!idOf(form?.programId)) {
    errors.programId = 'Choose a programme.';
  }
  const count = (selected ?? []).length;
  if (count === 0) {
    errors.athleteIds = 'Choose at least one athlete.';
  } else if (count > MAX_BULK_ATHLETES) {
    errors.athleteIds = `At most ${MAX_BULK_ATHLETES} athletes at once — you have ${count} chosen.`;
  }
  return errors;
}

/**
 * The request body for POST /assignments/bulk.
 *
 * Ids go in roster order rather than click order, so the assignments come back
 * in the order the coach sees the roster; duplicates and zeros are dropped
 * defensively, since either fails the whole request server-side.
 */
export function buildBulkAssignmentRequest(form, selected, roster) {
  const ticked = new Set((selected ?? []).map(idOf).filter(Boolean));
  const inRosterOrder = selectAll(roster).filter((id) => ticked.has(id));
  const strays = [...ticked].filter((id) => !inRosterOrder.includes(id));

  const body = {
    programId: idOf(form?.programId),
    athleteIds: [...inRosterOrder, ...strays],
  };

  const startDate = String(form?.startDate ?? '').trim();
  if (startDate) body.startDate = startDate;

  const notes = String(form?.notes ?? '').trim();
  if (notes) body.notes = notes;

  return body;
}
